import React, { useState, useEffect, useRef, useContext } from 'react';
import { BiMessage as Messaggio } from 'react-icons/bi';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { StateContext } from './StateContainer';
import {
  BsSearch as Cerca,
  BsFilter as FiltroIcon,
  BsHandThumbsUp,
  BsHandThumbsDown,
  BsXCircle,
} from 'react-icons/bs';
import Moment from 'react-moment';
import '../App.css';

const StoricoConversazioni = ({ projectCode }) => {
  const { id } = useParams();

  const [chatConversation, setChatConversation] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Filtri
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [thumbUp, setThumbUp] = useState(false);
  const [thumbDown, setThumbDown] = useState(false);

  const scrollRef = useRef();

  const {
    setError,
    token,
    setConversationCode,
    filtraConversazioni,
    setFiltraConversazioni,
  } = useContext(StateContext);

  const LinkChatStorico = (code) => {
    setConversationCode(code);
    setSelectedConversation(code);
  };

  const hasActiveFilters = startDate || endDate || thumbUp || thumbDown;

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
    setThumbUp(false);
    setThumbDown(false);
  };

  const applyFilters = () => {
    setChatConversation([]);
    setOffset(0);
    setHasMore(true);
    setInitialLoadDone(false);
    setFetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (!hasMore) return;

    const buildPayload = () => {
      const payload = {
        projectCode: id,
        limit: 20,
        offset: offset,
      };

      if (startDate) payload.startDate = startDate;
      if (endDate) payload.endDate = endDate;
      if (thumbUp) payload.thumbUp = true;
      if (thumbDown) payload.thumbDown = true;

      return payload;
    };

    const fetchData = () => {
      axios({
        method: 'post',
        url: `${process.env.REACT_APP_BASE_URL}/conversation/list`,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        data: buildPayload(),
      })
        .then((response) => {
          if (response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('tokenExpiration');
            window.location.replace('/login');
          } else {
            setError('');
            const newData = Array.isArray(response.data.response)
              ? response.data.response
              : [];
            setChatConversation((prev) => [...prev, ...newData]);
            if (newData.length < 20) {
              setHasMore(false);
            }
          }
        })
        .catch((err) => setError(err))
        .finally(() => {
          setLoading(false);
          setInitialLoadDone(true);
        });
    };

    if (offset === 0 && !initialLoadDone) {
      fetchData();
    } else {
      setLoading(true);
      setTimeout(fetchData, 1000);
    }
  }, [offset, fetchTrigger]);

  // Reset quando cambia il progetto
  useEffect(() => {
    setChatConversation([]);
    setOffset(0);
    setHasMore(true);
    setInitialLoadDone(false);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const el = scrollRef.current;
      if (!el || loading || !hasMore) return;

      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        setOffset((prev) => prev + 20);
      }
    };

    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', handleScroll);

    return () => {
      if (el) el.removeEventListener('scroll', handleScroll);
    };
  }, [loading, hasMore]);

  const risultatiFiltrati = chatConversation
    .filter((conversation) => {
      const search = filtraConversazioni.toLowerCase();
      if (filtraConversazioni === '') return true;
      return conversation.lastUserMessage?.toLowerCase().includes(search);
    })
    .sort((a, b) => {
      const dateA = new Date(a.lastMessageDate?.date ?? 0);
      const dateB = new Date(b.lastMessageDate?.date ?? 0);
      return dateB - dateA; // più recenti prima
    });

  return (
    <>
      <div
        className="card p-2"
        style={{ height: '100%', maxHeight: '800px', minHeight: '800px' }}
      >
        <div className="card-header bg-white border-0">
          <button
            type="button"
            className="btn-close text-reset button-close-offcanvas-storico d-block d-lg-none"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>

          <div className="d-flex align-items-center justify-content-between pt-3 pb-2">
            <h6 className="fw-bold m-0">Storico conversazioni</h6>
            <button
              className={`btn btn-sm d-flex align-items-center gap-1 ${hasActiveFilters ? 'btn-success' : 'btn-outline-secondary'}`}
              style={{ fontSize: '0.75rem' }}
              onClick={() => setShowFilters((prev) => !prev)}
              title="Mostra/nascondi filtri"
            >
              <FiltroIcon />
              Filtri
              {hasActiveFilters && (
                <span
                  className="badge bg-white text-success ms-1"
                  style={{ fontSize: '0.65rem' }}
                >
                  attivi
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div
              className="border rounded p-2 mb-2 bg-light"
              style={{ fontSize: '0.82rem' }}
            >
              <div className="row g-2 mb-2">
                <div className="col-6">
                  <label
                    className="form-label mb-1 text-muted fw-semibold"
                    style={{ fontSize: '0.75rem' }}
                  >
                    Data inizio
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label
                    className="form-label mb-1 text-muted fw-semibold"
                    style={{ fontSize: '0.75rem' }}
                  >
                    Data fine
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 mb-2">
                <span
                  className="text-muted fw-semibold"
                  style={{ fontSize: '0.75rem' }}
                >
                  Recensione:
                </span>
                <button
                  className={`btn btn-sm d-flex align-items-center gap-1 ${thumbUp ? 'btn-success' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  onClick={() => setThumbUp((prev) => !prev)}
                  title="Filtra per thumb-up"
                >
                  <BsHandThumbsUp />
                  Positiva
                </button>
                <button
                  className={`btn btn-sm d-flex align-items-center gap-1 ${thumbDown ? 'btn-danger' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                  onClick={() => setThumbDown((prev) => !prev)}
                  title="Filtra per thumb-down"
                >
                  <BsHandThumbsDown />
                  Negativa
                </button>
              </div>

              <div className="d-flex justify-content-end gap-2">
                {hasActiveFilters && (
                  <button
                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                    style={{ fontSize: '0.75rem' }}
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setThumbUp(false);
                      setThumbDown(false);
                      setChatConversation([]);
                      setOffset(0);
                      setHasMore(true);
                      setInitialLoadDone(false);
                      setFetchTrigger((prev) => prev + 1);
                    }}
                  >
                    <BsXCircle />
                    Resetta
                  </button>
                )}
                <button
                  className="btn btn-sm btn-primary"
                  style={{ fontSize: '0.75rem' }}
                  onClick={applyFilters}
                >
                  Applica filtri
                </button>
              </div>
            </div>
          )}

          <div className="form d-flex align-items-center">
            <p className="fa fa-search d-flex m-0">
              <Cerca className="m-0" />
            </p>
            <input
              type="text"
              className="form-control form-input"
              placeholder="Cerca conversazione ..."
              value={filtraConversazioni}
              onChange={(event) => setFiltraConversazioni(event.target.value)}
            />
          </div>
        </div>

        <div
          className="card-body archivio-conversazioni"
          style={{ overflowY: 'auto' }}
          ref={scrollRef}
        >
          <div>
            <h6 className="mt-2">Conversazioni precedenti</h6>
          </div>

          {chatConversation.length === 0 && !loading ? (
            <div className="fs-6 py-3 text-secondary">
              <i>Ancora nessuna conversazione</i>
            </div>
          ) : risultatiFiltrati.length === 0 ? (
            <div className="fs-6 py-3 text-secondary">
              <i>Nessun risultato trovato</i>
            </div>
          ) : (
            risultatiFiltrati.map((conversation, index) => {
              return (
                <div
                  className={`py-3 px-2 conversation_chat ${
                    selectedConversation === conversation.code
                      ? 'bg-verde text-white'
                      : ''
                  }`}
                  key={index}
                >
                  <div className="mb-1 d-flex align-items-center justify-content-between">
                    <p className="text-muted m-0" style={{ fontSize: '8pt' }}>
                      {conversation?.userUID !== '-' && (
                        <span>{conversation?.userUID} - </span>
                      )}
                      <Moment format="DD MMMM YYYY, HH:mm" locale="it">
                        {conversation.lastMessageDate.date}
                      </Moment>
                    </p>

                    <div className="d-flex gap-1">
                      {conversation.thumbUp && (
                        <span
                          title="Recensione positiva"
                          style={{ fontSize: '10pt', color: '#198754' }}
                        >
                          <BsHandThumbsUp />
                        </span>
                      )}
                      {conversation.thumbDown && (
                        <span
                          title="Recensione negativa"
                          style={{ fontSize: '10pt', color: '#dc3545' }}
                        >
                          <BsHandThumbsDown />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center gap-2 position-relative"
                      style={{ width: '1000px' }}
                    >
                      <Messaggio />
                      <p className="m-0">
                        {conversation.lastUserMessage &&
                        conversation.lastUserMessage.length > 30
                          ? conversation.lastUserMessage.substring(0, 30) +
                            ' ...'
                          : conversation.lastUserMessage}
                      </p>

                      <a
                        className="stretched-link btn"
                        onClick={() => LinkChatStorico(conversation.code)}
                      ></a>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {loading && initialLoadDone && (
            <div className="d-flex justify-content-center py-3">
              <div
                className="spinner-border text-secondary"
                role="status"
              ></div>
            </div>
          )}

          {!hasMore && (
            <div className="text-center py-3 text-secondary">
              <i>Fine conversazioni</i>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default StoricoConversazioni;
